import {
    AboveFoldScrollIndicator,
    Button,
    GridContainer,
    SectionInnerGridContent,
    Typography,
    useScroll,
    VerticalSpacer
} from '../../../';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import root from 'window-or-global';
import {MaskedHeaderProps} from './MaskedHeader';
import {MaskedHeaderGradient} from './MaskedHeader.gradient';
import {MaskedHeaderIcon} from './MaskedHeader.icon';
import {MaskedHeaderScrollIndicatorWrapper} from './MaskedHeader.styles';
import classNames from 'classnames';
import {motion, useMotionValue, useTransform} from 'framer-motion';

const Styled = styled.div`
    height: 300vh;
    width: 100%;
    position: relative;
    overflow: visible;
    z-index: 8;
    pointer-events: none;

    .gradient {
        /* cover the chins on mobile devices */
        min-height: -webkit-fill-available;
        bottom: -200px;
    }

    &.videoComplete {
        /* adjust for the chins on mobile devices */
        height: calc(300vh - 260px);

        .above-fold-hint {
            position: absolute;
            top: auto;
            bottom: 0;
        }
        .masked-video,
        .above-fold-heading {
            position: absolute;
            height: 100vh;
            min-height: initial;
            width: 100%;
            bottom: 0;
            left: 0;
            z-index: 0;
        }
        .above-fold-heading {
            z-index: 1;
            margin-top: 100vh;
        }
        .desc-section {
            display: block;
        }
    }

    .masked-video {
        pointer-events: none;
        position: fixed;
        width: 100vw;
        height: 100vh;
        min-height: -webkit-fill-available;
        bottom: 0;
        left: 0;
        video {
            height: 100%;
            width: 100%;
            object-fit: cover;
            aspect-ratio: 16 / 9;
        }
    }

    .above-fold-heading {
        pointer-events: none;
        position: fixed;
        bottom: 0;
        width: 100%;
        left: 0;
        right: 0;
        z-index: 100;
        margin: 0;
        margin-left: auto;
        margin-right: auto;
        text-align: center;
        top: 45%;
        left: 0;
        height: auto;

        .title-2 {
            display: none;
        }

        &.playVideo {
            .title-1 {
                display: none;
            }
            .title-2 {
                display: block;
            }
        }

        &.videoComplete {
            .title-2 {
                display: block;
            }
        }
    }
    .desc-section {
        display: none;
    }
`;

export const MaskedHeaderMobile = ({
    video,
    videoPlaceholder,
    title,
    title2,
    svg,
    description,
    button,
    accentColor
}: MaskedHeaderProps): JSX.Element => {
    const [vh, setVh] = useState(root.innerHeight || 1000);
    const [videoEnded, setVideoEnded] = useState(false);
    const [duration, setDuration] = useState<number>(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const {position} = useScroll();
    const scrollY = useMotionValue(0);
    scrollY.set(position.y);

    const initDuration = useCallback(() => {
        if (videoRef?.current?.duration && duration !== videoRef.current.duration) {
            setDuration(videoRef?.current?.duration || 0);
        }
    }, [duration, setDuration]);

    const setHeight = useCallback(() => {
        const newVh = root.innerHeight || 0;
        if (newVh !== vh) {
            setVh(newVh);
        }
    }, [vh, setVh]);

    const ended = useCallback(() => {
        setVideoEnded(true);
    }, [setVideoEnded]);

    useEffect(() => {
        setHeight();
        initDuration();

        root.addEventListener('resize', setHeight);
        return () => {
            root.removeEventListener('resize', setHeight);
        };
    }, [setHeight]);

    const videoCompletePosition = vh * 2;

    //translate out the title
    const textTranslate = useTransform(scrollY, [vh - 300, vh], [0, 80]);
    const textOpacity = useTransform(scrollY, [vh - 300, vh], [1, 0]);

    //translate in title2
    const title2TranslateIn = useTransform(
        scrollY,
        [vh + 100, videoCompletePosition - 300],
        [-100, 0]
    );
    const title2OpacityIn = useTransform(scrollY, [vh + 100, videoCompletePosition - 300], [0, 1]);

    let playVideo = false;
    if (vh && position.y >= vh) {
        playVideo = true;
    }

    let videoComplete = false;
    if (vh && position.y >= videoCompletePosition) {
        videoComplete = true;
    }

    useEffect(() => {
        if (!videoEnded && playVideo && videoRef && videoRef.current) {
            videoRef.current.play();
        }
    }, [videoRef, playVideo, videoEnded]);

    return (
        <>
            <Styled className={classNames({playVideo, videoComplete})}>
                <MaskedHeaderGradient />
                <MaskedHeaderIcon svg={svg} />

                <div className="masked-video">
                    <video
                        id="maskedVideo"
                        ref={videoRef}
                        onLoadedMetadata={initDuration}
                        onCanPlay={initDuration}
                        muted
                        autoPlay={false}
                        preload="auto"
                        loop={false}
                        onEnded={ended}
                        controls={false}
                        disablePictureInPicture
                        playsInline
                        poster={videoPlaceholder}>
                        <source src={video} type="video/mp4" />
                    </video>
                </div>

                <MaskedHeaderScrollIndicatorWrapper className={classNames({hide: videoComplete})}>
                    <AboveFoldScrollIndicator />
                </MaskedHeaderScrollIndicatorWrapper>

                <div className={classNames('above-fold-heading', {playVideo})}>
                    <motion.div
                        className="title-1 will-change-transform-opacity"
                        style={{
                            translateY: textTranslate,
                            opacity: textOpacity
                        }}>
                        <Typography variant="h1">{title || ''}</Typography>
                    </motion.div>
                    <motion.div
                        className="title-2 will-change-transform-opacity"
                        style={{
                            translateY: title2TranslateIn,
                            opacity: title2OpacityIn
                        }}>
                        <Typography variant="h1">{title2 || ''}</Typography>
                    </motion.div>
                </div>
            </Styled>
            <VerticalSpacer variant="section" />

            <div className={classNames('desc-section')}>
                {description ? (
                    <GridContainer key="ue-masked-header-desc">
                        <SectionInnerGridContent>
                            <VerticalSpacer variant="heading" />
                            <Typography variant="h2">{description}</Typography>
                            <VerticalSpacer variant="heading" />
                            <Button
                                type={button?.type || 'primary'}
                                href={button?.href}
                                color={accentColor}>
                                {button?.label}
                            </Button>
                        </SectionInnerGridContent>
                    </GridContainer>
                ) : (
                    ''
                )}
            </div>
            <VerticalSpacer variant="section" />
        </>
    );
};
